import React, { useState } from 'react';
import AppModal from '../AppModal/AppModal';
import './ErrorModal.css';

/**
 * ErrorModal - Displays error information with copy button and support email
 *
 * @param {boolean} isOpen - Whether the modal is visible
 * @param {function} onClose - Callback when modal is closed
 * @param {string} title - Modal title
 * @param {string} message - Error message to display
 * @param {number} statusCode - HTTP status code (optional)
 * @param {string} model - The AI model that was used (optional)
 * @param {function} onRetry - Callback for retry button (optional)
 * @param {string} retryLabel - Label for retry button (default: "Try Again")
 * @param {string} endpoint - The API endpoint that failed (optional)
 * @param {string} errorId - Unique error ID for tracking (optional)
 */
const ErrorModal = ({
    isOpen,
    onClose,
    title = "Something went wrong",
    message,
    statusCode,
    model,
    onRetry,
    retryLabel = "Try Again",
    endpoint,
    errorId
}) => {
    const [copied, setCopied] = useState(false);

    if (!isOpen) return null;

    const getStatusHint = (code) => {
        switch (code) {
            case 400: return "The request was invalid. Please check your input and try again.";
            case 401: return "Your session has expired. Please log in again.";
            case 403: return "You don't have permission to access this resource.";
            case 404: return "The requested resource could not be found.";
            case 429: return "Too many requests. Please wait a moment before trying again.";
            case 500: return "Our server encountered an error. We're working on it.";
            case 502: return "Service temporarily unavailable. Please try again shortly.";
            case 503: return "Service is overloaded. Please try again in a few minutes.";
            case 504: return "The request timed out. Please try again.";
            default: return null;
        }
    };

    const hint = statusCode ? getStatusHint(statusCode) : null;

    const getErrorDetails = () => {
        const timestamp = new Date().toISOString();
        return [
            `Timestamp: ${timestamp}`,
            statusCode ? `Status Code: ${statusCode}` : null,
            endpoint ? `Endpoint: ${endpoint}` : null,
            model ? `Model: ${model}` : null,
            errorId ? `Error ID: ${errorId}` : null,
            `Message: ${message}`,
            `URL: ${window.location.href}`,
            `User Agent: ${navigator.userAgent}`
        ].filter(Boolean).join('\n');
    };

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(getErrorDetails());
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            const textArea = document.createElement('textarea');
            textArea.value = getErrorDetails();
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand('copy');
            document.body.removeChild(textArea);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    };

    const getEmailBody = () => {
        const details = getErrorDetails();
        return encodeURIComponent(`Hi Alchemiq Support,\n\nI encountered an error while using the application.\n\n--- Error Details ---\n${details}\n\n--- Additional Context ---\n[Please describe what you were trying to do when this error occurred]\n\nThank you!`);
    };

    const emailSubject = encodeURIComponent(`Error Report: ${title}${statusCode ? ` (${statusCode})` : ''}`);
    const mailtoLink = `mailto:support@alchemiq.ai?subject=${emailSubject}&body=${getEmailBody()}`;

    const footer = (
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', marginTop: '20px' }}>
            <button
                type="button"
                onClick={onClose}
                style={{
                    padding: '10px 24px',
                    backgroundColor: '#fff',
                    color: '#3C4A5E',
                    border: '1px solid rgba(60,74,94,0.20)',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: 500,
                }}
            >
                Close
            </button>
            {onRetry && (
                <button
                    type="button"
                    onClick={onRetry}
                    style={{
                        padding: '10px 24px',
                        backgroundColor: '#FF6B35',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: 500,
                    }}
                >
                    {retryLabel}
                </button>
            )}
        </div>
    );

    return (
        <AppModal
            isOpen={isOpen}
            title={title}
            onCancel={onClose}
            footer={footer}
        >
            <p>{message}</p>

            {hint && <p style={{ color: '#3C4A5E', fontStyle: 'italic' }}>{hint}</p>}

            {(statusCode || model || endpoint) && (
                <div className="error-modal-details">
                    {statusCode && <span className="error-detail-badge error-status-badge">Status: {statusCode}</span>}
                    {model && <span className="error-detail-badge error-model-badge">Model: {model}</span>}
                    {endpoint && <span className="error-detail-badge error-endpoint-badge">{endpoint}</span>}
                </div>
            )}

            <div className="error-modal-support">
                <p className="error-support-text">
                    Oops, that wasn't supposed to happen! Please share the details with us so we can fix it.
                </p>
                <div className="error-support-actions">
                    <button
                        className={`error-copy-button ${copied ? 'copied' : ''}`}
                        onClick={handleCopy}
                        title="Copy error details to clipboard"
                    >
                        {copied ? (
                            <>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <polyline points="20 6 9 17 4 12"></polyline>
                                </svg>
                                Copied!
                            </>
                        ) : (
                            <>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                                </svg>
                                Copy Details
                            </>
                        )}
                    </button>
                    <a
                        href={mailtoLink}
                        className="error-email-button"
                        title="Email support with error details"
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                            <polyline points="22,6 12,13 2,6"></polyline>
                        </svg>
                        Contact Support
                    </a>
                </div>
            </div>
        </AppModal>
    );
};

export default ErrorModal;
